import React, {
  forwardRef,
  Fragment,
  ReactNode,
  RefObject,
} from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';

import { Icon } from '../common/Icon';
import { Separator } from '../common/Separator';
import { Touchable } from '../common/Touchable';
import { BaseText } from '../common/BaseText';
import {
  IPopoverProps,
  Popover,
  PopoverPositionEnum,
} from './Popover';
import { IPopoverManagerProps } from '@hooks';
import type { ColorType } from '@types';

const MenuContainer = styled(View)({
  width: 242,
  bg: '$background0',
  p: '$5',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  p: '$4',
  borderRadius: 8,
});

interface ILabelStyleProps {
  color?: ColorType;
}

const Label = styled(BaseText)((props: ILabelStyleProps) => ({
  color: props.color,
  variant: 'text.medium',
}));

type RefType = RefObject<IPopoverManagerProps>;

export type OptionMenuOptionsItemType = {
  label: string;
  icon: string;
  onPress(): void;
  color?: ColorType;
};

export type OptionMenuOptionsType = OptionMenuOptionsItemType[];

export interface IOptionsMenuPopoverProps
  extends Omit<IPopoverProps, 'children'> {
  children: ReactNode;
  items: OptionMenuOptionsType;
}

export const OptionsMenuPopover = forwardRef<
  IPopoverManagerProps,
  IOptionsMenuPopoverProps
>(
  (
    {
      children,
      items,
      position = PopoverPositionEnum.Left,
      ...props
    },
    ref,
  ) => {
    function selectOption(item: OptionMenuOptionsItemType) {
      (ref as RefType).current?.togglePopover();
      item.onPress();
    }

    return (
      <Popover ref={ref} position={position} {...props}>
        {children}

        <MenuContainer>
          {items.map((item, index) => {
            return (
              <Fragment key={index}>
                {index !== 0 && <Separator size="$4" />}

                <TouchableContainer
                  onPress={() => selectOption(item)}
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    color={item.color}
                  />
                  <Separator size="$4" vertical />
                  <Label color={item.color}>{item.label}</Label>
                </TouchableContainer>
              </Fragment>
            );
          })}
        </MenuContainer>
      </Popover>
    );
  },
);
